import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Botao,
  Campo,
  CampoDeFiltro,
  CelulaDeAcoes,
  EstadoErro,
  EstadoVazio,
  GrupoDeFiltro,
  MenuDeAcoes,
  OpcaoDeFiltro,
  Selo,
  Tabela,
  useDialogo,
  type ColunaTabela,
} from '../componentes/ui';
import { AvisoDoLote, BotaoDoLote, useAcaoEmLote } from '../componentes/AcaoEmLote';
import { useDesativarEmpresa } from '../hooks/useEscrita';
import { desativarEmpresa } from '../servicos/escrita';
import { resumirNomes } from '../servicos/lote';
import {
  listarEmpresasPorPerfil,
  type EmpresaComPerfis,
  type PerfilDeEmpresa,
  type PerfilDoFiltro,
  type ServicoDaOperacao,
} from '../servicos/empresas';
import { formatarCnpj } from '../servicos/formato';
import { todasAsPaginas, type ColunaDaPlanilha } from '../servicos/planilha';
import { BotaoExtrairXlsx } from '../componentes/BotaoExtrairXlsx';
import { AdicionarCliente } from './AdicionarCliente';
import { BotaoAdicionarCliente } from './BotaoAdicionarCliente';
import { Pagina } from './Carteira';
import { ErroDaApi } from './ErroDaApi';
import { VincularAoCrm, type EmpresaParaVincular } from './crm/VincularAoCrm';
import { rotaDaEmpresa } from './origem-da-empresa';

const POR_PAGINA = 25;

interface Aba {
  readonly rotulo: string;
  readonly descricao: string;
  readonly vazio: string;
}

const ABAS: Readonly<Record<PerfilDeEmpresa, Aba>> = {
  todas: {
    rotulo: 'Todas',
    descricao: 'Todas as empresas cadastradas, independentemente do papel que ocupam na operação.',
    vazio: 'Não há empresas cadastradas.',
  },
  clientes: {
    rotulo: 'Clientes',
    descricao: 'Empresas com perfil de cliente, e os serviços em que compraram crédito.',
    vazio: 'Nenhuma empresa tem perfil de cliente.',
  },
  assessores: {
    rotulo: 'Assessores',
    descricao: 'Empresas com perfil de assessor, e os serviços em que intermediaram.',
    vazio: 'Nenhuma empresa tem perfil de assessor.',
  },
  fornecedores: {
    rotulo: 'Fornecedores',
    descricao: 'Empresas com perfil de fornecedor, e os serviços em que venderam crédito.',
    vazio: 'Nenhuma empresa tem perfil de fornecedor.',
  },
};

/** As opções do "Filtro", na ordem em que aparecem. */
const OPCOES_DE_PERFIL: readonly { readonly valor: PerfilDoFiltro; readonly rotulo: string }[] = [
  { valor: 'clientes', rotulo: 'Clientes' },
  { valor: 'assessores', rotulo: 'Assessores' },
  { valor: 'fornecedores', rotulo: 'Fornecedores' },
  { valor: 'sem-perfil', rotulo: 'Sem perfil' },
];

/** O texto da página para o que está marcado — o de um papel só é o da antiga aba. */
function recorteDoFiltro(perfis: readonly PerfilDoFiltro[]): Aba {
  const [unico] = perfis;
  if (perfis.length === 0) return ABAS.todas;
  if (perfis.length === 1 && unico !== undefined && unico !== 'sem-perfil') return ABAS[unico];
  if (perfis.length === 1 && unico === 'sem-perfil') {
    return {
      rotulo: 'Sem perfil',
      descricao:
        'Empresas sem perfil de cliente, assessor ou fornecedor — ainda sem papel na operação.',
      vazio: 'Todas as empresas têm algum perfil.',
    };
  }
  return {
    rotulo: rotulosDos(perfis),
    descricao: 'Empresas com qualquer um dos perfis marcados no filtro.',
    vazio: 'Nenhuma empresa tem os perfis marcados.',
  };
}

function rotulosDos(perfis: readonly PerfilDoFiltro[]): string {
  return OPCOES_DE_PERFIL.filter((o) => perfis.includes(o.valor))
    .map((o) => o.rotulo)
    .join(', ');
}

/** O papel que cada aba lista — a chave de `servicos` que a coluna mostra. */
const PAPEL_DA_ABA = {
  clientes: 'cliente',
  assessores: 'assessor',
  fornecedores: 'fornecedor',
} as const;

const ROTULO_DO_SERVICO: Readonly<Record<ServicoDaOperacao, string>> = {
  RECORRENTE: 'ICMS Recorrente',
  PASSIVOS: 'ICMS Passivos',
  PONTUAL: 'ICMS Pontual',
};

/** As colunas da tabela, em valores de planilha — a de perfil/serviços segue a aba. */
function colunasDaPlanilha(perfil: PerfilDeEmpresa): ColunaDaPlanilha<EmpresaComPerfis>[] {
  const papelOuServicos: ColunaDaPlanilha<EmpresaComPerfis> =
    perfil === 'todas'
      ? {
          titulo: 'Perfil',
          valor: (e) =>
            [
              e.perfis.cliente && 'Cliente',
              e.perfis.assessor && 'Assessor',
              e.perfis.fornecedor && 'Fornecedor',
            ]
              .filter(Boolean)
              .join(', ') || 'Sem perfil',
        }
      : {
          titulo: `Serviços como ${PAPEL_DA_ABA[perfil]}`,
          valor: (e) =>
            e.servicos[PAPEL_DA_ABA[perfil]].map((s) => ROTULO_DO_SERVICO[s]).join(', '),
        };
  return [
    { titulo: 'Razão social', largura: 46, valor: (e) => e.legalName },
    { titulo: 'Nome fantasia', largura: 30, valor: (e) => e.name },
    { titulo: 'CNPJ', largura: 20, valor: (e) => formatarCnpj(e.taxpayerId) },
    { titulo: 'Cidade', largura: 22, valor: (e) => e.addressCity },
    { titulo: 'UF', largura: 6, valor: (e) => e.addressStateCode },
    { titulo: 'E-mail', largura: 32, valor: (e) => e.emailAddress },
    { ...papelOuServicos, largura: 30 },
    { titulo: 'Situação', largura: 12, valor: (e) => (e.isActive ? 'Ativa' : 'Desativada') },
  ];
}

/**
 * Os perfis marcados, da URL: `?perfis=clientes,fornecedores`. O `?perfil=` de
 * antes (uma aba só) continua valendo — é o que os links antigos carregam.
 * Valor desconhecido sai; a ordem é sempre a da lista.
 */
function perfisDaQuery(parametros: URLSearchParams): PerfilDoFiltro[] {
  const lidos = (parametros.get('perfis') ?? parametros.get('perfil') ?? '')
    .split(',')
    .map((p) => p.trim());
  return OPCOES_DE_PERFIL.map((o) => o.valor).filter((v) => lidos.includes(v));
}

/** Para onde a linha leva: o cliente abre no perfil; os demais, no cadastro. */
function rotaDaLinha(perfil: PerfilDeEmpresa, id: string): string {
  if (perfil === 'clientes') return `/clientes/${id}`;
  if (perfil === 'todas') return `/empresas/${id}`;
  return rotaDaEmpresa(id, perfil);
}

/**
 * Empresas, com as carteiras num "Filtro" de perfis.
 *
 * ## Uma tela, os recortes marcáveis
 *
 * Clientes, assessores e fornecedores são empresas — a mesma entidade com um
 * papel ligado. A lista de Empresas filtra pelo perfil: Clientes, Assessores,
 * Fornecedores e Sem perfil, marcáveis juntos (entra quem tem qualquer um dos
 * marcados). Eram abas de escolha única até 14/10/2026.
 *
 * Com **um** papel marcado, a tela é a da antiga aba: a coluna mostra **em que
 * serviços** a empresa atuou naquele papel, e a linha abre onde a aba abria.
 * Uma empresa pode estar em dois papéis com serviços diferentes: cliente no
 * Recorrente, fornecedora no Pontual.
 *
 * O serviço é deduzido da operação (contrato, SPED, negociação), não de um
 * cadastro: o sistema guarda o papel da empresa, mas não "papel por serviço".
 * Perfil sem operação mostra "Sem operação".
 *
 * O filtro vive na URL (`?perfis=`) para o "Voltar" do cadastro devolver ao
 * filtro de onde se veio, e para o filtro poder ser um link.
 *
 * As telas antigas de carteira (`/clientes`, `/fornecedores`, `/assessores`)
 * continuam de pé: o sino aponta para `/clientes?situacao=`, a lista de
 * contratos por etapa, que esta tela não tem.
 *
 * ## Aqui a busca é do servidor
 *
 * Esta lista tem centenas de registros e o endpoint já pagina e filtra. O termo
 * passa por um atraso antes de virar requisição: sem ele, "industria"
 * dispararia nove buscas, e as respostas poderiam chegar fora de ordem. A
 * página volta para 1 sempre que o termo ou o filtro mudam.
 *
 * ## "Nova empresa" é o assistente de cliente, sem o papel
 *
 * O assistente de `AdicionarCliente` faz CNPJ → dados → endereço com a consulta
 * à Receita; aqui ele roda em `modo="empresa"`, que não marca papel de cliente.
 * Com só Clientes marcado, o botão é o de cliente — manual ou por SPED.
 */
export function Empresas(): JSX.Element {
  const navegar = useNavigate();
  // `?busca=` vem do assistente quando o CNPJ já existe: ele manda para cá
  // com a razão social que a checagem devolveu.
  const [parametros, setParametros] = useSearchParams();
  const perfis = perfisDaQuery(parametros);
  const [unico] = perfis;
  // Um papel só: a tela da antiga aba (serviços do papel, rota da linha).
  const perfil: PerfilDeEmpresa =
    perfis.length === 1 && unico !== undefined && unico !== 'sem-perfil' ? unico : 'todas';
  const aba = recorteDoFiltro(perfis);
  const [busca, setBusca] = useState(parametros.get('busca') ?? '');
  const [formAberto, setFormAberto] = useState(false);
  const [buscaAplicada, setBuscaAplicada] = useState(busca);
  const [pagina, setPagina] = useState(1);
  const dialogo = useDialogo();
  const desativar = useDesativarEmpresa();
  const queryClient = useQueryClient();
  // "Vincular CRM" de uma linha ou das marcadas; a seleção sai quando a janela fecha.
  const [paraVincular, setParaVincular] = useState<{
    readonly empresas: readonly EmpresaParaVincular[];
    readonly limpar?: () => void;
  } | null>(null);
  // O mesmo "Excluir" da linha (RN-306), para as empresas marcadas.
  const excluirMarcadas = useAcaoEmLote<EmpresaComPerfis>({
    acao: (e) => desativarEmpresa(e.id),
    nome: (e) => e.legalName,
    aoTerminar: () => {
      void queryClient.invalidateQueries({ queryKey: ['empresas'] });
      void queryClient.invalidateQueries({ queryKey: ['carteira'] });
    },
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(busca);
      setPagina(1);
    }, 350);
    return () => clearTimeout(t);
  }, [busca]);

  const mudarPerfis = (novos: readonly PerfilDoFiltro[]): void => {
    setPagina(1);
    setParametros(
      (atual) => {
        const proximos = new URLSearchParams(atual);
        proximos.delete('perfil');
        if (novos.length === 0) proximos.delete('perfis');
        else proximos.set('perfis', novos.join(','));
        return proximos;
      },
      { replace: true },
    );
  };
  const alternarPerfil = (valor: PerfilDoFiltro): void =>
    mudarPerfis(
      // Na ordem da lista, sempre — a URL não depende da ordem do clique.
      OPCOES_DE_PERFIL.map((o) => o.valor).filter((v) =>
        v === valor ? !perfis.includes(v) : perfis.includes(v),
      ),
    );

  const consulta = useQuery({
    queryKey: ['empresas', 'perfis', perfis.join(','), buscaAplicada, pagina],
    queryFn: () =>
      listarEmpresasPorPerfil({ perfis, busca: buscaAplicada, pagina, porPagina: POR_PAGINA }),
    // Mantém a página anterior visível durante a troca, em vez de piscar para o
    // esqueleto a cada clique em "próxima" ou em outra aba.
    placeholderData: (anterior) => anterior,
  });

  const colunaDoPapel: ColunaTabela<EmpresaComPerfis> =
    perfil === 'todas'
      ? {
          chave: 'perfis',
          titulo: 'Perfil',
          larguraMinima: '14rem',
          celula: (e) => <SelosDePerfil empresa={e} />,
        }
      : {
          chave: 'servicos',
          titulo: `Serviços como ${PAPEL_DA_ABA[perfil]}`,
          larguraMinima: '16rem',
          celula: (e) => <SelosDeServico servicos={e.servicos[PAPEL_DA_ABA[perfil]]} />,
        };

  const colunas: readonly ColunaTabela<EmpresaComPerfis>[] = [
    {
      chave: 'legalName',
      titulo: 'Razão social',
      larguraMinima: '22rem',
      celula: (e) => (
        <span className="flex flex-col">
          <span className="text-conteudo-primario">{e.legalName}</span>
          {e.name !== null && e.name !== e.legalName && (
            <span className="text-xs text-conteudo-sutil">{e.name}</span>
          )}
        </span>
      ),
    },
    {
      chave: 'taxpayerId',
      titulo: 'CNPJ',
      larguraMinima: '11rem',
      celula: (e) => (
        <span className="valor-fiscal whitespace-nowrap text-sm">{formatarCnpj(e.taxpayerId)}</span>
      ),
    },
    colunaDoPapel,
    {
      chave: 'cidade',
      titulo: 'Cidade',
      larguraMinima: '12rem',
      celula: (e) =>
        e.addressCity === null ? (
          <span className="text-conteudo-sutil">—</span>
        ) : (
          `${e.addressCity}${e.addressStateCode === null ? '' : `/${e.addressStateCode}`}`
        ),
    },
    {
      chave: 'emailAddress',
      titulo: 'E-mail',
      larguraMinima: '16rem',
      celula: (e) => <span className="text-sm text-conteudo-secundario">{e.emailAddress}</span>,
    },
    // Item 7 de 15/09 — Excluir desativa (RN-306).
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      larguraMinima: '3rem',
      celula: (e) => (
        <CelulaDeAcoes>
          <MenuDeAcoes
            rotulo={e.legalName}
            itens={[
              { rotulo: 'Abrir', aoEscolher: () => navegar(rotaDaLinha(perfil, e.id)) },
              {
                rotulo: 'Vincular ao CRM',
                descricao: 'Cria um card dela no quadro e na coluna que você escolher.',
                aoEscolher: () => setParaVincular({ empresas: [{ id: e.id, nome: e.legalName }] }),
              },
              {
                rotulo: 'Excluir',
                perigo: true,
                descricao: 'Desativa a empresa e os vínculos dela.',
                aoEscolher: () => {
                  void (async () => {
                    const ok = await dialogo.confirmar({
                      titulo: `Excluir ${e.legalName}?`,
                      descricao:
                        'A empresa é desativada: sai das carteiras, os colaboradores perdem o vínculo e ela deixa de entrar em contrato. O cadastro e o histórico permanecem.',
                      rotuloConfirmar: 'Excluir',
                      tom: 'perigo',
                    });
                    if (ok) desativar.mutate(e.id);
                  })();
                },
              },
            ]}
          />
        </CelulaDeAcoes>
      ),
    },
  ];

  const dados = consulta.data;
  const total = dados?.total ?? 0;
  const ultimaPagina = Math.max(1, Math.ceil(total / POR_PAGINA));
  const vazia = !consulta.isLoading && (dados?.itens.length ?? 0) === 0;

  return (
    <Pagina
      titulo="Empresas"
      descricao={aba.descricao}
      contagem={total}
      acao={
        <div className="flex flex-wrap items-center gap-3">
          <Campo
            rotulo="Buscar empresa"
            rotuloOculto
            type="search"
            placeholder="Razão social, nome fantasia ou CNPJ"
            className="w-full sm:w-72"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          {/*
            A aba inteira, não a busca da tela: uma planilha que muda conforme o
            que está digitado ninguém reproduz depois. A lista é paginada no
            servidor, então a planilha busca todas as páginas.
          */}
          <CampoDeFiltro
            rotuloOculto
            className="w-full sm:w-64"
            semFiltro="Todos os perfis"
            marcados={OPCOES_DE_PERFIL.filter((o) => perfis.includes(o.valor)).map((o) => ({
              rotulo: o.rotulo,
              tirar: () => alternarPerfil(o.valor),
            }))}
            aoLimpar={() => mudarPerfis([])}
          >
            <GrupoDeFiltro
              titulo="Perfil"
              nota="Marcando mais de um, entra quem tem qualquer um deles."
            >
              {OPCOES_DE_PERFIL.map((o) => {
                const contagem = dados?.contagens[o.valor];
                return (
                  <OpcaoDeFiltro
                    key={o.valor}
                    rotulo={o.rotulo}
                    marcada={perfis.includes(o.valor)}
                    aoMudar={() => alternarPerfil(o.valor)}
                    {...(contagem === undefined ? {} : { contagem })}
                  />
                );
              })}
            </GrupoDeFiltro>
          </CampoDeFiltro>
          <BotaoExtrairXlsx
            titulo={perfis.length === 0 ? 'Empresas' : `Empresas - ${aba.rotulo}`}
            colunas={colunasDaPlanilha(perfil)}
            carregar={() =>
              todasAsPaginas((pagina) =>
                listarEmpresasPorPerfil({ perfis, busca: '', pagina, porPagina: 100 }),
              )
            }
          />
          {perfil === 'clientes' ? (
            <BotaoAdicionarCliente />
          ) : (
            <Botao onClick={() => setFormAberto(true)}>Nova empresa</Botao>
          )}
        </div>
      }
    >
      {consulta.isError ? (
        <EstadoErro
          titulo="Não foi possível carregar as empresas"
          mensagem={consulta.error.message}
          aoTentarNovamente={() => void consulta.refetch()}
        />
      ) : vazia ? (
        <EstadoVazio
          titulo="Nenhuma empresa encontrada"
          descricao={
            buscaAplicada.trim() === ''
              ? aba.vazio
              : `Nenhuma empresa corresponde a "${buscaAplicada.trim()}".`
          }
          {...(buscaAplicada.trim() === ''
            ? {}
            : {
                acao: (
                  <Botao variante="secundario" onClick={() => setBusca('')}>
                    Limpar busca
                  </Botao>
                ),
              })}
        />
      ) : (
        <>
          {desativar.isError && (
            <ErroDaApi titulo="Não foi possível excluir" erro={desativar.error} />
          )}
          <VincularAoCrm
            empresas={paraVincular?.empresas ?? null}
            aoFechar={() => {
              paraVincular?.limpar?.();
              setParaVincular(null);
            }}
          />
          <AvisoDoLote
            desfecho={excluirMarcadas.desfecho}
            feitos={(n) => (n === 1 ? '1 empresa excluída' : `${n} empresas excluídas`)}
            aoFechar={excluirMarcadas.limparDesfecho}
          />
          <Tabela
            legenda={`Empresas — ${aba.rotulo.toLowerCase()}`}
            colunas={colunas}
            dados={dados?.itens ?? []}
            chaveDaLinha={(e) => e.id}
            chaveDeCabecalhoDaLinha="legalName"
            carregando={consulta.isLoading}
            aoClicarNaLinha={(e) => navegar(rotaDaLinha(perfil, e.id))}
            selecao={{
              substantivo: { singular: 'empresa', plural: 'empresas', feminino: true },
              nomeDaLinha: (e) => e.legalName,
              acoes: (marcadas, limpar) => (
                <>
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    onClick={() =>
                      setParaVincular({
                        empresas: marcadas.map((e) => ({ id: e.id, nome: e.legalName })),
                        limpar,
                      })
                    }
                  >
                    Vincular CRM
                  </Botao>
                  <BotaoDoLote
                    rotulo="Excluir"
                    perigo
                    marcadas={marcadas}
                    limpar={limpar}
                    lote={excluirMarcadas}
                    confirmacao={(m) => ({
                      titulo:
                        m.length === 1
                          ? `Excluir ${m[0]!.legalName}?`
                          : `Excluir ${m.length} empresas?`,
                      descricao: `${resumirNomes(m.map((e) => e.legalName))}.\n\nCada uma é desativada: sai das carteiras, os colaboradores perdem o vínculo e ela deixa de entrar em contrato. O cadastro e o histórico permanecem.`,
                      rotuloConfirmar: 'Excluir',
                      tom: 'perigo',
                    })}
                  />
                </>
              ),
            }}
          />

          {ultimaPagina > 1 && (
            <nav
              aria-label="Paginação"
              className="flex items-center justify-between gap-4 border-t border-borda pt-4"
            >
              <p className="text-sm text-conteudo-sutil">
                Página {pagina} de {ultimaPagina}
              </p>
              <div className="flex gap-2">
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  disabled={pagina <= 1}
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </Botao>
                <Botao
                  variante="secundario"
                  tamanho="pequeno"
                  disabled={pagina >= ultimaPagina}
                  onClick={() => setPagina((p) => Math.min(ultimaPagina, p + 1))}
                >
                  Próxima
                </Botao>
              </div>
            </nav>
          )}
        </>
      )}

      <AdicionarCliente modo="empresa" aberto={formAberto} aoFechar={() => setFormAberto(false)} />
    </Pagina>
  );
}

/** Na aba Todas: os perfis da empresa, lado a lado. */
function SelosDePerfil(props: { readonly empresa: EmpresaComPerfis }): JSX.Element {
  const { perfis } = props.empresa;
  const ligados = [
    perfis.cliente && 'Cliente',
    perfis.assessor && 'Assessor',
    perfis.fornecedor && 'Fornecedor',
  ].filter((p): p is string => p !== false);

  if (ligados.length === 0) {
    return <span className="text-xs text-conteudo-sutil">Sem perfil</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {ligados.map((p) => (
        <Selo key={p} tom="informativo">
          {p}
        </Selo>
      ))}
    </span>
  );
}

/** Nas abas de papel: os serviços em que a empresa atuou naquele papel. */
function SelosDeServico(props: { readonly servicos: readonly ServicoDaOperacao[] }): JSX.Element {
  if (props.servicos.length === 0) {
    return (
      <span
        className="text-xs text-conteudo-sutil"
        title="Tem o perfil, mas ainda não aparece em contrato, SPED ou negociação."
      >
        Sem operação
      </span>
    );
  }
  return (
    <span className="flex flex-wrap gap-1">
      {props.servicos.map((s) => (
        <Selo key={s} tom="neutro">
          {ROTULO_DO_SERVICO[s]}
        </Selo>
      ))}
    </span>
  );
}
